import { sendDatadogRumError } from 'utils/analytics/datadogRum';

/**
 * Resolves an untrusted post-login `redirect_url` — it arrives from a query
 * string, an emailed magic link, or router location state — to a same-origin
 * relative path.
 *
 * Returns `undefined` for anything that does not resolve back to the app's own
 * origin, so callers fall through to their normal destination rather than
 * navigating off-site. Every backend producer builds these as `{APP_URL}/…`,
 * so same-origin is the full legitimate set.
 *
 * Resolving through `URL` — rather than a `startsWith('/')` check — is what
 * catches `//evil.com`, `/\evil.com` (browsers fold backslashes to slashes for
 * special schemes), `javascript:…`, and `https://app.collective.com@evil.com`.
 */
export function toSameOriginPath(redirectUrl?: string | null): string | undefined {
    if (!redirectUrl) {
        return undefined;
    }

    try {
        // Base off `href` rather than `origin` so the comparison reads from a
        // single location value.
        const base = new URL(window.location.href);
        const resolved = new URL(redirectUrl, base);

        if (resolved.origin !== base.origin) {
            sendDatadogRumError('UNSAFE_REDIRECT_URL_BLOCKED', new Error('Cross-origin redirect_url rejected'), {
                type: 'UNSAFE_REDIRECT_URL_BLOCKED',
                rejectedOrigin: resolved.origin,
            });
            return undefined;
        }

        return `${resolved.pathname}${resolved.search}${resolved.hash}`;
    } catch (e) {
        sendDatadogRumError('UNSAFE_REDIRECT_URL_BLOCKED', e, {
            type: 'UNSAFE_REDIRECT_URL_BLOCKED',
            rejectedOrigin: 'unparseable',
        });
        return undefined;
    }
}
